import React, { Fragment, useEffect, useState } from "react";
import { connect } from "react-redux";
import styled from "styled-components";

import EntityComponent from "../../common/entity-component";
import Snackbar from "../../common/Snackbar";

import config from "./config";
import UserForm from "./form";
import Store from "../../../types/Store";

import getAllAuthors from "../../../database/authors/getAllAuthors";
import { createAuthor } from "../../../database/authors/createAuthor";
import { Author } from "../../../types/Author";
import { editAuthor } from "../../../database/authors/editAuthor";
import StoreUser from "../../../types/StoreUser";
import { deleteAuthor } from "../../../database/authors/deleteAuthor";
import getCommunity from "../../../database/community/getCommunity";
import { Community } from "../../../types/Community";

import {
  DesignSystemSurface,
  PageHeader,
} from "../../../design-system";

const Page = styled.div`
  min-height: calc(100vh - 104px);
  padding: var(--space-6) var(--space-6) var(--space-16);
`;

const PageInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

type Props = {
  getAllUsers: Function;
  createUser: Function;
  users: Array<Object>;
  removeUser: Function;
  editUser: Function;
  store: Store;
  user: StoreUser;
};

const UserScreen = ({ user }: Props) => {
  let _passwordEdited = "";
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const [community, setCommunity] = useState<Community | null>(null);

  const authorResponse = getAllAuthors({ communityId: user?.communityId });

  useEffect(() => {
    // intentionally empty — snackbar wiring deferred
  }, [authorResponse]);

  useEffect(() => {
    if (!user?.communityId) return;
    getCommunity(user.communityId, setCommunity);
  }, [user?.communityId]);

  const onCreateUser = async (author: Author) => {
    createAuthor({ ...author, communityId: user?.communityId });
  };

  const onRemoveUser = ({ id }) => {
    deleteAuthor(id);
  };

  const onEditUser = (authorEdited: Author): void => {
    editAuthor(authorEdited.id, authorEdited);
  };

  const onEditPassword = (newPassword: string): void => {
    _passwordEdited = newPassword;
  };

  const renderSnackbar = (response: any): React.ReactNode => {
    const { message, error } = response;
    return (
      <Snackbar
        onCloseSnackbar={() => setIsSnackbarOpen(false)}
        isOpen={isSnackbarOpen}
        message={message}
        error={error}
      />
    );
  };

  const usernames = authorResponse.map((u) => u.fullName);

  return (
    <DesignSystemSurface>
      <Page>
        <PageInner>
          <PageHeader
            title="Authors"
            subtitle="People credited on your content."
          />
          <Fragment>
            <EntityComponent
              onRemoveItem={onRemoveUser}
              onCreateItem={onCreateUser}
              onEditItem={onEditUser}
              singularEntityName="Author"
              pluralEntityName="Authors"
              filterConfig={config.filterConfig}
              tabConfig={config.tabConfig}
              dataset={authorResponse}
              canBeRemoved
              canBeCreated
              canBeEdited
              Form={(props) => (
                <UserForm
                  onEditPassword={onEditPassword}
                  usernames={usernames}
                  communityId={user?.communityId}
                  community={community}
                  {...props}
                />
              )}
            />
            {renderSnackbar(authorResponse)}
          </Fragment>
        </PageInner>
      </Page>
    </DesignSystemSurface>
  );
};

const mapStateToProps = (state) => ({
  store: state.store,
  user: state.storeUser,
});

export default connect(mapStateToProps, null)(UserScreen);
